import React, { useCallback, useMemo, useState } from 'react';

import { Urls } from 'js/src/client';
import { PrimaryButton, SecondaryButton } from 'js/src/components/common/ui/Button';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import Modal from 'js/src/components/common/ui/modal';
import Select from 'js/src/components/common/ui/Select/Select';
import { trackUpsellClicked } from 'js/src/libs/analytics-utils';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';

export interface DurationOption {
    value: number;
    label: React.ReactNode;
    locked?: boolean;
}

interface DurationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (seconds: number) => void;
    initialDuration?: number | null;
    title?: string;
    description?: string;
    durationOptions?: DurationOption[];
    header?: React.ReactNode;
    upgradeAnalyticsEvent?: string;
}

const isUserOnFreePlan = (): boolean => {
    return SERVER.USER?.plan_price <= 0;
};

const isDurationLocked = (seconds: number): boolean => {
    return isUserOnFreePlan() && seconds > 120;
};

export const DurationModal: React.FC<DurationModalProps> = ({
    isOpen,
    onClose,
    onApply,
    initialDuration,
    title = 'Select duration target',
    description = 'Select your target video duration. The final video length may vary slightly based on your content.',
    durationOptions,
    header,
    upgradeAnalyticsEvent,
}) => {
    const isFreePlan = isUserOnFreePlan();

    const handleUpgradeClick = useCallback(() => {
        window.open(Urls.PRICING, '_blank');
        if (upgradeAnalyticsEvent) {
            analytics.track(upgradeAnalyticsEvent);
        }
    }, [upgradeAnalyticsEvent]);

    const generatedDurationOptions = useMemo(
        () =>
            Array.from({ length: 20 }, (_, i) => {
                const seconds = (i + 1) * 15;
                const isLocked = isDurationLocked(seconds);
                return {
                    value: seconds,
                    label: getTimeStringFromSeconds(seconds),
                    locked: isLocked,
                };
            }),
        [],
    );

    const finalDurationOptions = durationOptions || generatedDurationOptions;

    const upsellCallout = isFreePlan ? (
        <Callout
            msgType={CALLOUT_TYPES.UPSELL}
            className="mt-l5-16"
            title="Want to create longer videos?"
            text="Upgrade to a premium plan to create videos longer than 2 minutes."
            ctaText="Upgrade"
            onCtaClick={handleUpgradeClick}
        />
    ) : undefined;

    const handleLockedOptionClick = useCallback(() => {
        trackUpsellClicked('duration_modal_locked_option');
        handleUpgradeClick();
    }, [handleUpgradeClick]);

    const [selectedDuration, setSelectedDuration] = useState(
        initialDuration || finalDurationOptions[0].value,
    );

    const handleDurationChange = useCallback((value: number) => {
        setSelectedDuration(value);
    }, []);

    const handleApply = useCallback(() => {
        onApply(selectedDuration);
    }, [onApply, selectedDuration]);

    return (
        <Modal
            showing={isOpen}
            onHidden={onClose}
            title={title}
            footer={
                <>
                    <SecondaryButton text="Cancel" onClick={onClose} />
                    <PrimaryButton text="Apply" onClick={handleApply} />
                </>
            }
        >
            <div className="p-l5-16">
                {header}

                <div className="mb-l5-12 color-grey-700">
                    <p className="mb-l5-8">{description}</p>
                </div>

                <div style={{ minHeight: '240px' }}>
                    <Select<number>
                        value={finalDurationOptions.find(
                            (option) => option.value === selectedDuration,
                        )}
                        options={finalDurationOptions}
                        onChange={handleDurationChange}
                        upgradeLockClicked={handleLockedOptionClick}
                        isMulti={false}
                        menuPlacement="bottom"
                    />
                </div>

                {upsellCallout}
            </div>
        </Modal>
    );
};
